import type { Visit } from '@services/requests/housien-models/Visit';
import type { VisitInput } from '@services/requests/housien-models/VisitInput';
import type { useDataStateOverride } from '@/shared/ui';

export type VisitKanbanStatus = 'pending' | 'confirmed' | 'done' | 'cancelled';

export interface KanbanVisit {
  id: string;
  date: string;
  time: string;
  property: string;
  propertyShort: string;
  propertyAddress: string;
  client: string;
  clientPhone?: string;
  broker: string;
  brokerContact: string;
  status: VisitKanbanStatus;
  notes?: string;
  propertyIds: string[];
  raw: Visit;
}

const STATUS_MAP: Record<string, VisitKanbanStatus> = {
  pending: 'pending',
  confirmed: 'confirmed',
  done: 'done',
  cancelled: 'cancelled',
};

export const STATUS_MAP_REVERSE: Record<VisitKanbanStatus, NonNullable<VisitInput['status']>> = {
  pending: 'pending',
  confirmed: 'confirmed',
  done: 'done',
  cancelled: 'cancelled',
};

/**
 * Parses an ISO datetime into `date`/`time` parts, shifting +3 hours to render
 * in Brasília time. Returns empty parts for missing or unparseable input.
 *
 * @param isoString - ISO datetime string to parse.
 * @returns The `date` (`YYYY-MM-DD`) and `time` (`HH:MM`) in BRT.
 */
function parseVisitDatetime(isoString: string): { date: string; time: string } {
  if (!isoString) return { date: '', time: '' };
  try {
    const d = new Date(isoString);
    d.setHours(d.getHours() + 3);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return { date, time };
  } catch {
    return { date: '', time: '' };
  }
}

/**
 * Resolves a human-readable property label from a properties-map entry, falling
 * back to a composed street/number/neighborhood string or the raw ID.
 *
 * @param data - Property record from the properties map.
 * @param id - Property ID used as the last-resort label.
 * @returns The best available display label.
 */
function resolvePropertyLabel(data: Record<string, unknown> | undefined, id: string): string {
  if (!data) return id || '—';
  const desc = data.description as Record<string, unknown> | undefined;
  if (desc?.headline) return desc.headline as string;
  const loc = data.location as Record<string, unknown> | undefined;
  const composed = [loc?.street, loc?.number, loc?.neighborhood].filter(Boolean).join(', ');
  return composed || id;
}

/**
 * Composes a full address string (street, number, neighborhood, city) from a
 * properties-map entry, omitting missing parts.
 *
 * @param data - Property record from the properties map.
 * @returns The comma-joined address, or an empty string when no data.
 */
function resolvePropertyAddress(data: Record<string, unknown> | undefined): string {
  if (!data) return '';
  const loc = data.location as Record<string, unknown> | undefined;
  if (!loc) return '';
  return [loc.street, loc.number, loc.neighborhood, loc.city].filter(Boolean).join(', ');
}

/**
 * Composes a short property label (street + number only) for compact card
 * display. Falls back to headline or the full label when no location data.
 *
 * @param data - Property record from the properties map.
 * @param id - Property ID used as the last-resort label.
 * @returns A short display label suitable for a card subtitle.
 */
function resolvePropertyShort(data: Record<string, unknown> | undefined, id: string): string {
  if (!data) return id || '—';
  const loc = data.location as Record<string, unknown> | undefined;
  const short = [loc?.street, loc?.number].filter(Boolean).join(', ');
  const desc = data.description as Record<string, unknown> | undefined;
  const candidates = [short, desc?.headline as string | undefined];
  return candidates.find(Boolean) || id || '—';
}

/**
 * Maps a backend {@link Visit} to the flat {@link KanbanVisit} shape the board
 * renders, resolving BRT date/time, property label/address, client, and broker
 * fields with sensible fallbacks.
 *
 * @param v - Backend visit record.
 * @param propertiesMap - Optional map of property ID to property data for label resolution.
 * @returns The kanban-friendly visit.
 */
export function toKanbanVisit(v: Visit, propertiesMap?: Record<string, unknown>): KanbanVisit {
  const { date, time } = parseVisitDatetime(v.visit_datetime ?? '');
  const propertyId = v.property_ids?.[0] ?? '';
  const propertyData = propertiesMap?.[propertyId] as Record<string, unknown> | undefined;

  return {
    id: v.visit_id ?? '',
    date,
    time,
    property: resolvePropertyLabel(propertyData, propertyId),
    propertyShort: resolvePropertyShort(propertyData, propertyId),
    propertyAddress: resolvePropertyAddress(propertyData),
    client: v.client_name ?? v.client_id ?? '—',
    clientPhone: v.client_phone,
    broker: v.broker_name ?? v.owner_email ?? v.broker_emails?.[0] ?? '—',
    brokerContact: v.owner_email ?? v.broker_emails?.[0] ?? '',
    status: STATUS_MAP[v.status ?? ''] ?? 'pending',
    notes: v.notes ?? undefined,
    propertyIds: v.property_ids ?? [],
    raw: v,
  };
}

export interface UseVisitsKanbanReturn {
  visits: KanbanVisit[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  updateStatus: (visitId: string, newStatus: VisitKanbanStatus, extraFields?: Record<string, unknown>) => Promise<void>;
}

interface VisitsFlags {
  visits: KanbanVisit[];
  isLoading: boolean;
  error: string | null;
}

interface VisitsActions {
  refetch: () => void;
  updateStatus: UseVisitsKanbanReturn['updateStatus'];
}

/**
 * Applies a dev-cockpit data-state override to the kanban result, forcing empty
 * visits, a loading flag, or a simulated error while preserving the actions.
 *
 * @param override - Active data-state override ('empty' | 'loading' | 'error' | undefined).
 * @param flags - Real loading/error/visits state.
 * @param actions - Refetch and status-update callbacks passed through unchanged.
 * @returns The kanban return object reflecting the override.
 */
export function applyVisitsOverride(
  override: ReturnType<typeof useDataStateOverride>,
  flags: VisitsFlags,
  actions: VisitsActions,
): UseVisitsKanbanReturn {
  return {
    visits: override === 'empty' ? [] : flags.visits,
    isLoading: flags.isLoading || override === 'loading',
    error: override === 'error' ? 'error.simulated' : flags.error,
    ...actions,
  };
}

/**
 * Normalizes a visits payload that may arrive as an array or a keyed object into
 * a flat array of {@link Visit}.
 *
 * @param raw - The raw visits payload.
 * @returns The visits as an array.
 */
export function normalizeVisitsData(raw: unknown): Visit[] {
  if (Array.isArray(raw)) return raw;
  return Object.values(raw ?? {}) as Visit[];
}

interface VisitsFetchCallbacks {
  fetchVisits: () => Promise<{ data?: unknown }>;
  buildMap: () => Promise<Record<string, unknown>>;
  onSuccess: (visits: KanbanVisit[]) => void;
  onError: (message: string) => void;
  onSettled: () => void;
  fallbackError: string;
}

/**
 * Runs the visits + properties fetch, maps rows to {@link KanbanVisit}, and
 * routes results through the provided callbacks. Returns a cleanup function
 * that cancels late responses.
 */
export function runVisitsFetch(cb: VisitsFetchCallbacks): () => void {
  let cancelled = false;
  Promise.all([cb.fetchVisits(), cb.buildMap()])
    .then(([result, propMap]) => {
      if (cancelled) return;
      cb.onSuccess(normalizeVisitsData(result.data).map((v) => toKanbanVisit(v, propMap)));
    })
    .catch((err: unknown) => {
      if (cancelled) return;
      cb.onError(err instanceof Error ? err.message : cb.fallbackError);
    })
    .finally(() => { if (!cancelled) cb.onSettled(); });
  return () => { cancelled = true; };
}
